import { AbstractDate } from "@linkfluence/models/date";
import { Timezone } from "@linkfluence/models/timezone";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
  isImageClusteringDataviz,
} from "@radarly/models/dataviz";
import { Card, ClusterCard } from "..";
import { ClusterFragment } from "@radarly/models/search";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";

interface ClusterCardFactoryOptions {
  clause?: Clause;
  from?: AbstractDate;
  to?: AbstractDate;
  tz?: Timezone;
}

export class ClusterCardFragmentsFactory extends CardFragmentsFactory {
  static create(input?: ClusterCardFactoryOptions) {
    const factory = new ClusterCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card) {
    const factory = new ClusterCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
    });
  }
  private getFragments(input?: ClusterCardFactoryOptions): [ClusterFragment] {
    return [this.getFragment(input)];
  }
  private getFragment(input?: ClusterCardFactoryOptions): ClusterFragment {
    return {
      request: {
        query: this.getQuery(input),
      },
      type: "cluster",
    };
  }
}
export class ClusterCardFactory
  implements ICardFactory<ClusterCard, ClusterCardFactoryOptions>
{
  public static getInstance() {
    return new ClusterCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: ClusterCardFactoryOptions & {
      dataviz?: ClusterCard["dataviz"];
    }
  ) {
    const fragments = ClusterCardFragmentsFactory.createFromCard(card);
    const dataviz = this.createDataviz(input?.dataviz || card.dataviz);
    return CardFactory.getInstance<ClusterCard>().createCard(
      workspaceId,
      projectFocuses,
      "cluster",
      card.title,
      [this.getClusterFragment(fragments[0])],
      dataviz
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: ClusterCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    const fragments = ClusterCardFragmentsFactory.create(input);
    const dataviz = this.createDataviz(
      input?.fullDataviz ??
        getDefaultDataviz(input?.dataviz || DatavizName.ImageClustering)
    );
    return CardFactory.getInstance<ClusterCard>().createCard(
      workspaceId,
      projectFocuses,
      "cluster",
      title,
      [this.getClusterFragment(fragments[0])],
      dataviz
    );
  }
  private getClusterFragment(fragment: ClusterFragment) {
    return {
      request: fragment.request,
      type: "cluster" as ClusterFragment["type"],
    };
  }

  public createDataviz(dataviz: Dataviz) {
    if (isImageClusteringDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(
        DatavizName.ImageClustering
      ) as ClusterCard["dataviz"];
    }
  }
}
